import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../redux/store";

import {
  listenToBrandProductsFeed,
  stopBrandProductsFeed,
} from "../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../redux/storeCartSlice";
import { listenToCommerceData } from "../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../redux/storeProductRatingSlice";
import { logout } from "../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";
import appLogo from "../../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   ⭐ CATEGORY SOURCE — the SAME collection StoreProducts saves
   with. Products store `categoryId` (a CategoriesCollection id)
   + `attributes` (a record keyed by attribute id). This page —
   scoped to ONE brand — walks that same tree to build a
   department → category → deep-node browser containing only the
   nodes this brand actually has live products in.
═══════════════════════════════════════════════════════════ */
import {
  DEPARTMENTS,
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, ShoppingBag, X, ChevronRight, Plus, Minus,
  Heart, User, ShoppingCart, MapPin,
  ChevronDown, LogOut, ArrowLeft,
  Package, Home, ShoppingBasket, Star, LayoutGrid, Check,
  Tag, Sparkles,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as StoreProductsHome
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  black: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
  divider: "#F3E6CE",
};

const T = {
  pageBg: HEART.bg,
  cardBg: HEART.panelBg,
  black: HEART.dark,
  white: HEART.panelBg,
  offwhite: HEART.dark,
  muted: HEART.textMuted,
  mutedDim: HEART.textFaint,
  red: HEART.red,
  redDim: HEART.redSoft,
  border: HEART.border,
  grad: `linear-gradient(135deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  gradH: `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  outline: `1.5px solid ${HEART.border}`,
};

const GRAD_H = T.gradH;
const RGBA_RED = (a: number) => `rgba(200,32,46,${a})`;

/* accent comes straight from the brand — no random tints */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || HEART.red;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS
   CategoriesCollection ids are hierarchical strings, but the
   deeper levels (PT-…, PRD-…) break the prefix scheme — so we
   ALWAYS resolve ancestry by walking parentId, never substrings.
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

/** ancestors + self, memoized (this runs per product per render otherwise) */
const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | null): string[] => {
  if (!id) return [];
  const hit = chainCache.get(id);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(id);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(id, chain);
  return chain;
};

const catName = (id?: string | null) => (id ? CAT_MAP.get(id)?.name ?? "" : "");
const catPathLabel = (id?: string | null) =>
  chainIdsOf(id).map((cid) => catName(cid)).join(" › ");

/** the DEPARTMENT id an arbitrary category id ultimately rolls up to */
const departmentIdOf = (id?: string | null): string | null => {
  const chain = chainIdsOf(id);
  for (const cid of chain) {
    if (DEPARTMENTS.some((d) => d.id === cid)) return cid;
  }
  return null;
};

/** SAME mapping StoreProducts uses to bridge into the attributes
    tree ("0" + nearest category/department ancestor id). */
const toAttributeCategoryId = (id?: string | null): string => {
  if (!id) return "";
  const chain = chainIdsOf(id)
    .map((cid) => CAT_MAP.get(cid)!)
    .filter(Boolean);
  const anchor = [...chain]
    .reverse()
    .find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

/** Resolve a saved attribute value (option id, array of ids, or free
    text) into a human label using the attribute definition. */
const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

/** Attributes whose options make them a buyable variant (Color, Size, etc). */
const isVariantAttr = (attr: any) =>
  (attr?.inputType === "select" || !attr?.inputType) &&
  Array.isArray(attr?.options) &&
  attr.options.length > 0;

/** Variant attribute defs for a product, resolved via the attributes tree. */
const variantAttrsForItem = (item: any) => {
  const defs = attributesForCategory(toAttributeCategoryId(item?.categoryId)) ?? [];
  return defs.filter(isVariantAttr);
};

const hasVariants = (item: any) => variantAttrsForItem(item).length > 0;

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";

/* deterministic accent per department, matches StoreProductsHome */
const DEPT_ACCENTS = [
  "#8E44AD", "#455A64", "#0277BD", "#F48FB1", "#C9A84C", "#3F51B5",
  "#1565C0", "#43A047", "#1B0E06", "#FF6A00", "#6D4C41", "#6A1B9A",
  "#E91E63", "#FFB300", "#D84315", "#37474F", "#8D6E63", "#C8202E",
  "#388E3C", "#607D8B", "#FFC107",
];
const deptAccent = (deptId: string) => {
  const idx = DEPARTMENTS.findIndex((d) => d.id === deptId);
  return DEPT_ACCENTS[idx >= 0 ? idx % DEPT_ACCENTS.length : 0];
};

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — incl. the ✨ fabulous oval toggle bar ✨
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&family=Archivo+Black&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }
input::placeholder { color: rgba(27,14,6,.35); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes titleGlow  { 0%,100%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 14px 30px rgba(200,32,46,.18);} 50%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 18px 40px rgba(200,32,46,.30);} }
@keyframes ovalSheen {
  0%   { transform: translateX(-160%) skewX(-18deg); }
  60%,100% { transform: translateX(320%) skewX(-18deg); }
}

.p-card { transition: box-shadow .22s, transform .22s; }
.p-card:hover { box-shadow: 0 4px 10px rgba(0,0,0,.06), 0 24px 48px rgba(0,0,0,.16); transform: translateY(-6px); }

.add-btn-3d { transition: transform .18s ease, box-shadow .18s ease; }
.add-btn-3d:hover  { transform: translateY(-3px); box-shadow: 0 10px 24px var(--shadow) !important; }
.add-btn-3d:active { transform: translateY(0) scale(.96); box-shadow: 0 4px 14px var(--shadow) !important; }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:${T.mutedDim}; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:${T.red}; }

.svh-title {
  font-family: 'Archivo Black','Montserrat',sans-serif;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 0.95;
  background: linear-gradient(180deg, ${HEART.dark} 0%, ${HEART.dark} 60%, ${HEART.red} 130%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: titleGlow 3.4s ease-in-out infinite;
}

.svh-hbtn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.16);
         border:1px solid rgba(255,255,255,.3);cursor:pointer;color:#fff;
         font-size:12px;font-weight:700;padding:9px 14px;border-radius:999px;
         flex-shrink:0;white-space:nowrap;transition:background .2s;font-family:inherit;}
.svh-hbtn:hover{background:rgba(255,255,255,.26);}

/* ══════════════════════════════════════════════
   ✨ THE OVAL — one continuous glassy capsule
   holding this brand's live departments.
══════════════════════════════════════════════ */
.svh-oval {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 10px;
  border-radius: 999px;
  background:
    linear-gradient(#FFFDF8, #FFF6E6) padding-box,
    linear-gradient(120deg, ${HEART.gold}, ${HEART.red} 45%, ${HEART.gold} 90%) border-box;
  border: 2px solid transparent;
  box-shadow:
    0 10px 30px rgba(200,32,46,.14),
    0 2px 6px rgba(27,14,6,.08),
    inset 0 1px 0 rgba(255,255,255,.9);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.svh-oval::after {
  content: "";
  position: absolute;
  top: 0; bottom: 0; left: 0;
  width: 90px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
  animation: ovalSheen 5.5s ease-in-out infinite;
  pointer-events: none;
}
.svh-oval-toggle {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .03em;
  color: ${HEART.textMuted};
  background: transparent;
  transition: color .18s, transform .18s;
  white-space: nowrap;
}
.svh-oval-toggle:hover { color: ${HEART.dark}; transform: translateY(-1px); }
.svh-oval-toggle .svh-oval-ic { display: flex; font-size: 13px; transition: transform .18s; }
.svh-oval-toggle:hover .svh-oval-ic { transform: rotate(-8deg) scale(1.12); }
.svh-oval-toggle.active {
  color: #fff;
  background: linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
  box-shadow: 0 8px 18px rgba(200,32,46,.42), inset 0 1px 0 rgba(255,255,255,.28);
}
.svh-oval-toggle.active .svh-oval-ic { transform: none; }

.svh-scg-tile{background:none;border:none;cursor:pointer;padding:0;border-radius:14px;overflow:hidden;}

.nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer; font-size:14px; font-weight:700; color:${T.offwhite}; background:none; width:100%; text-align:left; border:none; border-bottom:1px solid ${HEART.divider}; transition:background .15s; }
.nav-item:hover { background: ${HEART.goldSoft}; }
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR / PAGE STYLES
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard: { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(27,14,6,.06)", border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  category: { padding: 10, borderRadius: 12, background: HEART.bg, marginTop: 8, border: `1px solid ${HEART.border}`, cursor: "pointer" },
  row: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub: { paddingLeft: 20, paddingTop: 8 },
  sidebar: { background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20, boxShadow: "0 4px 24px rgba(27,14,6,.08)", position: "sticky" as const, top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const, border: T.outline },
};

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Montserrat',sans-serif", position: "relative" as const },
  content: { position: "relative" as const, zIndex: 1 },
  headerTop: { background: GRAD_H, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_RED(0.25)}` },
  searchWrap: { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: `1.5px solid rgba(255,255,255,.32)` },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn: { width: 42, minHeight: 40, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs: { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: T.cardBg, display: "flex", justifyContent: "space-around", alignItems: "center", borderTop: `1px solid ${HEART.border}`, zIndex: 100 },
};

/* ═══════════════════════════════════════════════════════════
   PAGE WATERMARK
═══════════════════════════════════════════════════════════ */
const PageWatermark: React.FC<{ src: string }> = ({ src }) => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
      backgroundImage: `url(${src})`, backgroundRepeat: "repeat",
      backgroundSize: "170px 170px", opacity: 0.045, filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT — brand-scoped store
═══════════════════════════════════════════════════════════ */
const Store: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => (s as any).auth?.authLoaded ?? true);
  const userId = authUser?.userId ?? "";

  /* brand-scoped product feed (storeProducts.brandProducts) */
  const brandProducts = useSelector((s: RootState) => (s as any).storeProducts?.brandProducts ?? []);
  const brandLoading = useSelector((s: RootState) => (s as any).storeProducts?.brandProductsLoading ?? false);

  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector(
    (s: RootState) => (s as any).storeProductRatings?.summaries ?? {}
  );

  /* the brand itself (name, logo, address) */
  const partner = useSelector((s: RootState) => (s as any).commercePartners?.currentPartner);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* ── active department (master toggle) — set from live products ── */
  const [activeDept, setActiveDept] = useState<string | null>(null);

  /* any-level filter selections inside the active department */
  const [activeCat, setActiveCat] = useState<string | null>(null);   // category level
  const [activeDeep, setActiveDeep] = useState<string | null>(null); // any deeper level

  /* sidebar drill state */
  const [openDeptPanel, setOpenDeptPanel] = useState<string | null>(null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  /* product detail modal */
  const [qty, setQty] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* brand info + brand-scoped product feed */
  useEffect(() => {
    if (!brandId) return;
    const unsub = dispatch(listenToCommerceData(brandId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [brandId, dispatch]);

  useEffect(() => {
    if (!brandId) return;
    dispatch(listenToBrandProductsFeed(brandId) as any);
    dispatch(fetchAllProductSummaries() as any);
    return () => { dispatch(stopBrandProductsFeed() as any); };
  }, [brandId, dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  /* only this brand's published products */
  const liveProducts = useMemo(
    () => (brandProducts || []).filter((l: any) => l.isPublished !== false),
    [brandProducts]
  );

  /* ═══════════════════════════════════════════════════════════
     LIVE CATEGORY DISCOVERY — every category id that appears in
     the ancestry chain of a live product. This is what scopes the
     department oval, the icon bar, the tree and the tiles to just
     what THIS brand actually sells.
  ═══════════════════════════════════════════════════════════ */
  const { liveNodeIds, liveDeptIds, countByNode } = useMemo(() => {
    const nodes = new Set<string>();
    const depts = new Set<string>();
    const count = new Map<string, number>();
    liveProducts.forEach((l: any) => {
      const chain = chainIdsOf(l.categoryId);
      chain.forEach((cid) => {
        nodes.add(cid);
        count.set(cid, (count.get(cid) ?? 0) + 1);
        if (DEPARTMENTS.some((d) => d.id === cid)) depts.add(cid);
      });
    });
    return { liveNodeIds: nodes, liveDeptIds: depts, countByNode: count };
  }, [liveProducts]);

  /* departments this brand has, in DEPARTMENTS order */
  const liveDepartments = useMemo(
    () => DEPARTMENTS.filter((d) => liveDeptIds.has(d.id)),
    [liveDeptIds]
  );

  /* default the active department to the first one the brand has */
  useEffect(() => {
    if (!liveDepartments.length) { setActiveDept(null); return; }
    setActiveDept((prev) =>
      prev && liveDepartments.some((d) => d.id === prev) ? prev : liveDepartments[0].id
    );
    setOpenDeptPanel((prev) =>
      prev && liveDepartments.some((d) => d.id === prev) ? prev : liveDepartments[0].id
    );
  }, [liveDepartments]);

  const dept = useMemo(
    () => liveDepartments.find((d) => d.id === activeDept) ?? liveDepartments[0] ?? null,
    [liveDepartments, activeDept]
  );

  /* children of a node that the brand actually has products in */
  const liveChildrenOf = (parentId: string | null) =>
    childrenOf(parentId).filter((c) => liveNodeIds.has(c.id));

  const deptCategories = useMemo(
    () => (dept ? liveChildrenOf(dept.id) : []),
    [dept, liveNodeIds]
  );

  /* ── handlers ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
  };

  const handleDeptSelect = (deptId: string) => {
    setActiveDept(deptId);
    setActiveCat(null); setActiveDeep(null);
    setOpenDeptPanel(deptId);
    setOpenNodes(new Set());
  };

  const handleCatSelect = (catId: string) => {
    setActiveCat((prev) => (prev === catId ? null : catId));
    setActiveDeep(null);
  };

  const handleDeepSelect = (id: string) => setActiveDeep(id);

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const getQty = (id: string) =>
    cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const isOutOfStock = (item: any) =>
    item?.available === false || (item?.stock ?? 0) <= 0;

  const openProductModal = (item: any) => {
    setDetailItem(item); setQty(1); setSelectedVariants({}); setDetailOpen(true);
  };
  const closeProductModal = () => { setDetailOpen(false); setDetailItem(null); };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    // products with selectable variants must be configured in the modal
    if (hasVariants(item)) { openProductModal(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;

    const vDefs = variantAttrsForItem(detailItem);
    const missing = vDefs.some((a: any) => selectedVariants[String(a.id)] == null);
    if (missing) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart({
      listingId: detailItem.listingId, name: detailItem.name!,
      rate: detailItem.rate!, qty,
      photo: detailItem.photos?.[0],
      hostId: detailItem.hostId, brandId: detailItem.brandId,
      selectedVariants: { ...selectedVariants },
    } as any));
    closeProductModal(); setQty(1); setSelectedVariants({});
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  /* ⭐ checkout — /pay with userId + items, same as StoreProductsHome */
  const goToCheckout = () =>
    navigate(`/pay/${userId}`, { state: { items: cartItems } });

  const handleLogout = async () => {
    await dispatch(logout() as any);
    navigate("/AuthSwitch");
  };

  /* ═══════════════════════════════════════════════════════════
     FILTERING — a product belongs to the active department (and
     optional category / deep node) if that node id appears in the
     ancestry chain of the product's saved categoryId.
  ═══════════════════════════════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = liveProducts.filter((l: any) => {
      const chain = chainIdsOf(l.categoryId);
      if (activeDept && !chain.includes(activeDept)) return false;
      if (activeCat && !chain.includes(activeCat)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${(l.tags ?? []).join(" ")} ${l.vendor ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [liveProducts, activeDept, activeCat, activeDeep, searchQuery, activeSort]);

  const visibleProductCount = filteredProducts.length;
  const activeFilterName = activeDeep ? catName(activeDeep) : activeCat ? catName(activeCat) : null;

  /* brand info */
  const brandAccent = brandAccentColor(partner);
  const shopName = partner?.name || "Store";
  const shopLogo = partner?.image || appLogo;
  const shopCity = partner?.address?.city || null;
  const shopCountry = partner?.address?.country || null;

  /* lock scroll while modal open */
  useEffect(() => {
    if (!detailOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailOpen]);

  /* ═══════════════════════════════════
     HEADER — back · brand · search · categories · cart · profile
  ═══════════════════════════════════ */
  const HeaderBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      <button className="svh-hbtn" onClick={() => navigate(-1)} style={{ padding: "9px 11px" }}>
        <ArrowLeft size={15} />
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <div style={{ background: "#fff", borderRadius: "50%", padding: 2, display: "inline-flex" }}>
          <img src={shopLogo} alt={shopName}
            style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </div>
        {!isMobile && (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>{shopName}</span>
            {(shopCity || shopCountry) && (
              <span style={{ fontSize: 10, color: "rgba(255,255,255,.85)", display: "flex", alignItems: "center", gap: 3 }}>
                <MapPin size={9} color="rgba(255,255,255,.85)" />
                {[shopCity, shopCountry].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={S.searchWrap}>
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${shopName}…`}
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button style={S.searchBtn}><Search size={16} color="#fff" /></button>
      </div>

      {liveDepartments.length > 0 && (
        <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="svh-hbtn">
          <LayoutGrid size={14} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
        </button>
      )}

      <button onClick={goToCheckout} className="svh-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: HEART.redDark, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="svh-hbtn"><User size={15} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 200, background: "#fff", borderRadius: 16, border: T.outline, boxShadow: "0 16px 40px rgba(27,14,6,.2)", overflow: "hidden", zIndex: 400 }}
              >
                <button className="nav-item" onClick={() => navigate(`/profile/${userId}`)}><User size={15} /> Profile</button>
                <button className="nav-item" onClick={() => navigate(`/glamourclientdashboard/${userId}`)}><ShoppingBag size={15} /> Orders</button>
                <button className="nav-item" style={{ color: T.red, borderBottom: "none" }} onClick={handleLogout}><LogOut size={15} /> Sign out</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     🎬 STORE TITLE HERO
  ═══════════════════════════════════ */
  const DeptTitleBanner = () => (
    <div style={{ padding: isMobile ? "6px 4px 4px" : "4px 4px 8px", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
      <span className="svh-title" style={{ fontSize: isMobile ? 30 : 46 }}>
        {dept?.name ?? shopName}
      </span>
      <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim, textTransform: "uppercase" }}>
        {shopCity ? `${shopName} · ${shopCity}` : shopName}
      </span>
    </div>
  );

  /* ═══════════════════════════════════
     ✨ THE OVAL — only this brand's live departments
  ═══════════════════════════════════ */
  const OvalToggleBar = () => {
    if (liveDepartments.length <= 1) return null;
    return (
      <div style={{ padding: isMobile ? "0 4px 14px" : "0 4px 18px" }}>
        <div className="svh-oval">
          <span style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, padding: "0 6px 0 8px", color: HEART.goldDark }}>
            <Sparkles size={14} />
          </span>
          {liveDepartments.map((d) => {
            const isActive = d.id === activeDept;
            return (
              <button
                key={d.id}
                className={`svh-oval-toggle${isActive ? " active" : ""}`}
                onClick={() => handleDeptSelect(d.id)}
              >
                <span className="svh-oval-ic" style={{ color: isActive ? "#fff" : deptAccent(d.id) }}>
                  {d.icon}
                </span>
                {d.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════════
     SIDEBAR TREE — recursive drill, brand-scoped: only nodes with
     live products render. Any node can be picked as a filter;
     nodes with (live) children also expand.
  ═══════════════════════════════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = liveChildrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const isPicked = activeCat === node.id || activeDeep === node.id;

    return (
      <div style={depth === 0 ? A.category : { paddingLeft: 14, paddingTop: 7 }}>
        <div
          style={{ ...A.row, fontSize: depth === 0 ? 14 : 13, color: isPicked ? T.red : T.black }}
          onClick={() => {
            if (depth === 0) handleCatSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}
        >
          <span style={{ display: "flex", color: isPicked ? T.red : (dept ? deptAccent(dept.id) : T.red) }}>{node.icon}</span>
          {node.name}
          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: T.mutedDim }}>{countByNode.get(node.id) ?? 0}</span>
          {kids.length > 0 && (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}>
              <ChevronRight size={13} color={T.mutedDim} />
            </motion.div>
          )}
          {isPicked && kids.length === 0 && <Check size={13} color={T.red} style={{ marginLeft: "auto" }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <div style={A.sub}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const DeptCategoryTree: React.FC = () => {
    if (!dept) return null;
    const isOpen = openDeptPanel === dept.id;
    return (
      <div style={A.commerceCard}>
        <div style={A.commerceHeader} onClick={() => setOpenDeptPanel(isOpen ? null : dept.id)}>
          {dept.name}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              {deptCategories.length === 0 ? (
                <p style={{ fontSize: 12, color: T.mutedDim, paddingTop: 10 }}>No sub-categories here yet.</p>
              ) : (
                deptCategories.map((cat) => <TreeNode key={cat.id} node={cat} depth={0} />)
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <>
      <OvalToggleBar />
      <DeptCategoryTree />
    </>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: HEART.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${HEART.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.red }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     CATEGORY ICON BAR — active dept's live categories
  ═══════════════════════════════════ */
  const CategoryIconBar = () => {
    if (!deptCategories.length || !dept) return null;
    const accent = deptAccent(dept.id);
    return (
      <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: HEART.panelBg, borderRadius: 18, border: T.outline }}>
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          {/* All */}
          <button onClick={() => { setActiveCat(null); setActiveDeep(null); }}
            style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
            <div style={{
              width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%",
              background: activeCat === null ? GRAD_H : "#fff",
              border: `2px solid ${activeCat === null ? T.red : HEART.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: activeCat === null ? `0 8px 20px ${RGBA_RED(0.3)}` : "none",
            }}>
              <ShoppingBasket size={isMobile ? 20 : 24} color={activeCat === null ? "#fff" : accent} />
            </div>
            <span style={{ fontSize: 10, fontWeight: activeCat === null ? 800 : 600, color: activeCat === null ? T.red : T.black }}>All</span>
          </button>

          {deptCategories.map((cat) => {
            const isSel = activeCat === cat.id;
            return (
              <button key={cat.id}
                onClick={() => handleCatSelect(cat.id)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px", position: "relative" }}>
                <div style={{
                  width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%", overflow: "hidden",
                  border: `2px solid ${isSel ? T.red : HEART.border}`,
                  boxShadow: isSel ? `0 0 0 3px ${T.redDim}` : "none", transition: "all .2s",
                }}>
                  {cat.image ? (
                    <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85, transition: "opacity .2s" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", display: "flex", fontSize: isMobile ? 18 : 22 }}>{cat.icon}</span>
                    </div>
                  )}
                </div>
                {(countByNode.get(cat.id) ?? 0) > 1 && (
                  <span style={{ position: "absolute", top: -2, right: -2, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff" }}>
                    {(countByNode.get(cat.id) ?? 0) > 99 ? "99+" : countByNode.get(cat.id)}
                  </span>
                )}
                <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: isSel ? T.red : T.black, whiteSpace: "nowrap" }}>{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — live children as 4-up tiles (filter, not nav)
  ═══════════════════════════════════ */
  const ShopByCategory = () => {
    if (!deptCategories.length || !dept) return null;

    const focusId = activeCat ?? deptCategories[0]?.id ?? dept.id;
    const focusKids = liveChildrenOf(focusId);
    if (!focusKids.length) return null;

    const chunk4 = (arr: CategoryItem[]) => {
      const out: CategoryItem[][] = [];
      for (let i = 0; i < arr.length; i += 4) out.push(arr.slice(i, i + 4));
      return out;
    };
    const focusName = focusId === dept.id ? dept.name : catName(focusId);
    const chunks = chunk4(focusKids);
    const cardGroups = chunks.map((tiles, idx) => ({
      title: chunks.length > 1 ? `${focusName} ${idx + 1}` : (focusName || "All"),
      tiles,
    }));

    return (
      <div style={{ marginBottom: 28 }}>
        <div onClick={() => setShopCatExpanded((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>Shop by Category</span>
          <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}><ChevronDown size={20} color={T.muted} /></motion.div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div key={`${dept.id}-${focusId}`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group, gi) => (
                  <div key={`${group.title}-${gi}`}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 20, overflow: "hidden", border: T.outline, boxShadow: "0 6px 28px rgba(27,14,6,.06)", transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${T.redDim}, 0 16px 38px rgba(27,14,6,.16)`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = "0 6px 28px rgba(27,14,6,.06)"; el.style.borderColor = HEART.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -0.2 }}>{group.title}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.tiles.map((node: CategoryItem) => {
                        const sel = activeCat === node.id || activeDeep === node.id;
                        const hasKids = liveChildrenOf(node.id).length > 0;
                        return (
                          <button key={node.id} className="svh-scg-tile"
                            onClick={() => handleDeepSelect(node.id)}
                            style={{ padding: 0, display: "flex", flexDirection: "column" }}>
                            <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: HEART.bg, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2, transition: "outline .15s" }}>
                              {node.image ? (
                                <>
                                  <img src={node.image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${HEART.dark}EE, transparent 55%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 5 : 7, left: 4, right: 4, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.15, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>{node.name}</span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 4 }}>
                                  <span style={{ fontSize: isMobile ? 22 : 26, color: T.red, display: "flex" }}>{node.icon}</span>
                                  <span style={{ fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: T.black, textAlign: "center", lineHeight: 1.15 }}>{node.name}</span>
                                </div>
                              )}
                              {sel ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              ) : hasKids ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ChevronRight size={10} color={T.red} />
                                </div>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT CARD — click opens the in-page detail modal
  ═══════════════════════════════════ */
  const ProductCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const q = getQty(item.listingId);
    const outOfStock = isOutOfStock(item);
    const price = `${symbol ?? "€"}${((item.rate ?? 0) * (rate ?? 1)).toFixed(2)}`;
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const accent = brandAccent;
    const isWished = wished.has(item.listingId);

    const overlap = isMob ? 58 : 100;
    const imgZoneH = isMob ? 200 : 300;

    return (
      <motion.div className="p-card" whileTap={{ scale: 0.96 }}
        style={{ borderRadius: 20, overflow: "visible", background: "#fff", boxShadow: "0 1px 2px rgba(27,14,6,.05), 0 10px 28px rgba(27,14,6,.10)", cursor: "pointer", display: "flex", flexDirection: "column", height: "100%", marginTop: overlap }}
        onClick={() => openProductModal(item)}>
        <div style={{ width: "100%", height: imgZoneH, marginTop: -overlap, position: "relative", zIndex: 4, overflow: "visible", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {item.photos?.[0] ? (
            <div style={{ width: "115%", height: "115%", transform: "translateY(-5%)" }}>
              <motion.img src={item.photos[0]} alt={item.name} whileHover={{ scale: 1.06 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom", display: "block", filter: "drop-shadow(0 16px 20px rgba(27,14,6,.25))" }} />
            </div>
          ) : (
            <div style={{ width: "60%", height: "70%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={isMob ? 40 : 50} color={T.mutedDim} style={{ opacity: 0.35 }} />
            </div>
          )}

          <button onClick={(e) => toggleWish(item.listingId, e)}
            style={{ position: "absolute", top: overlap - 4, right: 4, background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.12)", zIndex: 6 }}>
            <Heart size={13} color={isWished ? T.red : T.muted} fill={isWished ? T.red : "none"} />
          </button>

          {discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 999, zIndex: 5 }}>{discount}% OFF</div>
          )}
          {hasRating && (
            <div style={{ position: "absolute", top: overlap + (discount ? 30 : 8), left: 4, zIndex: 6, background: "rgba(255,255,255,.92)", border: `1px solid ${T.border}`, borderRadius: 999, padding: isMob ? "3px 7px" : "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={isMob ? 9 : 10} fill={T.black} color={T.black} />
              <span style={{ color: T.black, fontWeight: 800, fontSize: isMob ? 9 : 10 }}>{summary!.avgRating.toFixed(1)}</span>
              <span style={{ color: T.muted, fontSize: isMob ? 8 : 9 }}>({summary!.totalReviews.toLocaleString()})</span>
            </div>
          )}
        </div>

        <div style={{ padding: isMob ? "2px 12px 12px" : "0px 16px 16px", display: "flex", flexDirection: "column", gap: isMob ? 2 : 3, flex: 1, justifyContent: "flex-end" }}>
          {item.category && (
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: T.mutedDim, display: "flex", alignItems: "center", gap: 4 }}>
              <Tag size={9} /> {catName(item.categoryId) || item.category}
            </span>
          )}
          <div style={{ fontWeight: 800, fontSize: isMob ? 13 : 15, color: T.black, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: isMob ? 19 : 24 }}>
            {item.name}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontWeight: 900, fontSize: isMob ? 16 : 19, color: T.black }}>{price}</span>
            {discount && (
              <span style={{ fontSize: isMob ? 11 : 12, color: T.mutedDim, textDecoration: "line-through" }}>
                {symbol ?? "€"}{((item.compareAtPrice ?? 0) * (rate ?? 1)).toFixed(2)}
              </span>
            )}
          </div>

          {outOfStock ? (
            <div style={{
              width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", borderRadius: 999, marginTop: isMob ? 2 : 4,
              border: `2px solid ${T.mutedDim}`, display: "flex", alignItems: "center", justifyContent: "center",
              background: HEART.bg, color: T.mutedDim, fontWeight: 800, fontSize: isMob ? 11 : 13, letterSpacing: "0.06em",
            }}>
              OUT OF STOCK
            </div>
          ) : q > 0 ? (
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="add-btn-3d"
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: `2px solid ${accent}`, borderRadius: 999, padding: "0 6px", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, marginTop: isMob ? 2 : 4 }}>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={isMob ? 12 : 14} color="#fff" />
              </button>
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 13 : 15 }}>{q}</span>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}>
                <Plus size={isMob ? 12 : 14} color="#fff" />
              </button>
            </motion.div>
          ) : (
            <motion.button className="add-btn-3d" whileTap={{ scale: 0.96 }}
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", borderRadius: 999, marginTop: isMob ? 2 : 4, border: `2px solid ${accent}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}>
              <ShoppingCart size={isMob ? 13 : 15} color={accent} />
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 11 : 13, letterSpacing: "0.06em" }}>ADD TO BAG</span>
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  };

  const EmptyState = ({ title, body }: { title: string; body: string }) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>{title}</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{body}</p>
      {(searchQuery || activeCat || activeDeep) && (
        <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>Clear filters</button>
      )}
    </div>
  );

  /* ── products grid ── */
  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeFilterName ?? (dept?.name ? `All ${dept.name}` : "All Products")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: HEART.goldSoft, padding: "2px 9px", borderRadius: 999 }}>{visibleProductCount}</span>
        </div>
        <select
          value={activeSort}
          onChange={(e) => setActiveSort(e.target.value as SortKey)}
          style={{ border: T.outline, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: T.muted, background: "#fff", cursor: "pointer", outline: "none" }}
        >
          <option value="default">Featured</option>
          <option value="newest">Newest</option>
          <option value="price_asc">Price: low → high</option>
          <option value="price_desc">Price: high → low</option>
          <option value="name_asc">Name A–Z</option>
        </select>
      </div>

      {brandLoading && !liveProducts.length ? (
        <div style={{ textAlign: "center", padding: 56 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", border: `3px solid ${T.redDim}`, borderTopColor: T.red, animation: "gradSpin .8s linear infinite", margin: "0 auto 14px" }} />
          <p style={{ color: T.muted, fontSize: 13 }}>Loading products…</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        liveProducts.length === 0 ? (
          <EmptyState title="No products yet" body="This store hasn't listed any products yet — check back soon." />
        ) : (
          <EmptyState title={searchQuery ? "No results found" : "Nothing here yet"} body={searchQuery ? `No products match "${searchQuery}".` : "Try another category from this store."} />
        )
      ) : (
        <div style={isMob ? S.mobileGrid : S.grid}>
          {filteredProducts.map((item: any) => <ProductCard key={item.listingId} item={item} isMob={isMob} />)}
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     PRODUCT DETAIL MODAL — variants + attributes card (same as SPH)
  ═══════════════════════════════════ */
  const DetailModal = () => {
    if (!detailOpen || !detailItem) return null;
    const brandName = shopName;
    const previewImg = detailItem?.photos?.[0];
    const stock = detailItem?.stock ?? 0;
    const outOfStock = isOutOfStock(detailItem);
    const discount = detailItem.compareAtPrice && detailItem.compareAtPrice > (detailItem.rate ?? 0)
      ? Math.round(((detailItem.compareAtPrice - (detailItem.rate ?? 0)) / detailItem.compareAtPrice) * 100) : null;

    /* resolve saved attributes → variant pickers + readable detail chips */
    const attrDefs = attributesForCategory(toAttributeCategoryId(detailItem.categoryId)) ?? [];
    const savedAttrs = detailItem.attributes ?? {};
    const variantDefs = attrDefs.filter(isVariantAttr);
    const detailDefs = attrDefs.filter((a: any) => !isVariantAttr(a));

    const attrChips = detailDefs
      .map((a: any) => ({ name: a.name, value: resolveAttrValue(a, savedAttrs[String(a.id)]) }))
      .filter((c) => c.value);

    const allVariantsChosen = variantDefs.every(
      (a: any) => selectedVariants[String(a.id)] != null
    );
    const canContinue = !outOfStock && qty > 0 && qty <= stock && allVariantsChosen;

    const CloseBtn = () => (
      <button onClick={closeProductModal}
        style={{ background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.18)" }}>
        <X size={15} color={T.black} />
      </button>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "85vh" : "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: HEART.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg ? (
            <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", borderRadius: 14, overflow: "hidden", marginBottom: 16, background: HEART.bg }}>
              <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {discount && (
                <div style={{ position: "absolute", top: 10, left: 10, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999 }}>{discount}% OFF</div>
              )}
              <div style={{ position: "absolute", top: 10, right: 10 }}><CloseBtn /></div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}><CloseBtn /></div>
          )}

          {brandName && (
            <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: T.red, textTransform: "uppercase", marginBottom: 4 }}>{brandName}</p>
          )}
          <div style={{ fontSize: 15, fontWeight: 800, color: T.black, marginBottom: 4 }}>{detailItem?.name}</div>

          {detailItem?.categoryId && (
            <p style={{ fontSize: 10.5, fontWeight: 700, color: T.mutedDim, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
              <Tag size={10} /> {catPathLabel(detailItem.categoryId)}
            </p>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 18 }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: T.black }}>
              {symbol ?? "€"}{((detailItem?.rate ?? 0) * (rate ?? 1)).toFixed(2)}
            </span>
            {discount && (
              <span style={{ fontSize: 13, color: T.mutedDim, textDecoration: "line-through" }}>
                {symbol ?? "€"}{((detailItem?.compareAtPrice ?? 0) * (rate ?? 1)).toFixed(2)}
              </span>
            )}
          </div>

          {/* ── selectable variants (Color, Size, …) ── */}
          {variantDefs.map((attr: any) => {
            const key = String(attr.id);
            const chosen = selectedVariants[key];
            return (
              <div key={key} style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, textTransform: "uppercase" }}>{attr.name}</span>
                  {chosen == null && <span style={{ fontSize: 10, fontWeight: 700, color: T.red }}>Please select</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {attr.options.map((opt: any) => {
                    const sel = String(chosen) === String(opt.id);
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setSelectedVariants((prev) => ({ ...prev, [key]: opt.id }))}
                        style={{
                          padding: "8px 14px", borderRadius: 999, cursor: "pointer",
                          fontSize: 12, fontWeight: 700,
                          border: `1.5px solid ${sel ? T.red : T.border}`,
                          background: sel ? T.redDim : "#fff",
                          color: sel ? T.red : T.black, transition: "all .15s",
                        }}
                      >
                        {opt.name || opt.standard || String(opt.id)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── saved attributes → cute DETAILS card ── */}
          {attrChips.length > 0 && (
            <div style={{ background: HEART.goldSoft, border: `1px solid ${HEART.border}`, borderRadius: 16, padding: "14px 16px", marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                <span style={{ width: 24, height: 24, borderRadius: 8, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: HEART.goldDark, flexShrink: 0 }}>
                  <Sparkles size={13} />
                </span>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: HEART.goldDark, textTransform: "uppercase" }}>Details</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {attrChips.map((c) => (
                  <div key={c.name} style={{ background: "#fff", border: `1px solid ${HEART.border}`, borderRadius: 12, padding: "8px 11px", display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: HEART.goldDark, textTransform: "uppercase", letterSpacing: ".04em" }}>{c.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.black }}>{c.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detailItem?.vendor && (
            <p style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
              <strong style={{ color: T.black }}>Vendor:</strong> {detailItem.vendor}
            </p>
          )}
          {detailItem?.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {detailItem.tags.map((t: string) => (
                <span key={t} style={{ fontSize: 10, fontWeight: 700, color: T.muted, background: HEART.bg, border: `1px solid ${HEART.border}`, borderRadius: 999, padding: "3px 10px" }}>#{t}</span>
              ))}
            </div>
          )}

          {detailItem?.description && (
            <div
              style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 18 }}
              dangerouslySetInnerHTML={{ __html: detailItem.description }}
            />
          )}

          {outOfStock ? (
            <p style={{ fontSize: 11, fontWeight: 700, color: T.mutedDim, marginBottom: 10 }}>Out of stock</p>
          ) : stock <= 5 ? (
            <p style={{ fontSize: 11, fontWeight: 700, color: T.red, marginBottom: 10 }}>Only {stock} left</p>
          ) : null}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: HEART.bg }}>
              <button onClick={() => setQty((v) => Math.max(1, v - 1))} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty((v) => Math.min(stock || 1, v + 1))} disabled={qty >= stock} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: qty >= stock ? "not-allowed" : "pointer", fontSize: 16, color: T.red, opacity: qty >= stock ? 0.4 : 1 }}>+</button>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45 }}>
            Add to Bag
          </button>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: "none", background: canContinue ? GRAD_H : "#C8C8C8", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={closeProductModal}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>{content}</motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, borderRadius: 20, overflow: "hidden", boxShadow: "0 32px 80px rgba(27,14,6,.35)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Home", active: false, action: () => navigate(`/StoreProductsHome/${userId}`) },
        { icon: <ShoppingBasket size={22} />, label: "Stores", active: true, action: () => navigate("/Stores") },
        {
          label: "Cart", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>}
            </div>
          ), action: goToCheckout
        },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/glamourclientdashboard/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`} style={{ position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : T.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark src={shopLogo} />

      <div style={S.content}>
        <header style={S.headerTop}>
          <HeaderBar />
        </header>

        <MobileCategorySheet />

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && (
              <aside style={A.sidebar}>
                <DeptCategoryTree />
              </aside>
            )}
            <main style={{ minWidth: 0 }}>
              <DeptTitleBanner />
              <OvalToggleBar />
              <CategoryIconBar />
              <ShopByCategory />
              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            <DeptTitleBanner />
            <OvalToggleBar />
            <CategoryIconBar />
            <div style={{ marginTop: 16 }}><ShopByCategory /></div>
            {renderProducts(true)}
          </div>
        )}

        <DetailModal />
        <BottomTabs />
      </div>
    </div>
  );
};

export default Store;
